// @ts-strict

import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@env/environment';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { BaseApiResponse, DefaultApiType, IActionButtonData, IAlert, IEnlargeSectionData, ISortMethodData, PageableApiResponse } from '../../index';

import {
  ICreateTrafficSourceData,
  IEditTrafficSourceData,
  ISimpleTrafficSourcesResponse,
  ITrafficSource,
  ITrafficSourceCampaignData,
  TrafficSourceDetailsResponse,
  TrafficSourcesListParams
} from '@services/traffic-sources/traffic-sources.types';
import { Faq } from '@services/support-panel/support-panel.types';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root'
})
export class TrafficSourcesService {
  public actionButtonElements: IActionButtonData[] = [
    { icon: 'basket', class: '', external: false, text: 'delete' },
    { icon: 'copy', class: '', external: false, text: 'details' }
  ];

  public verificationButtonElements: IActionButtonData[] = [{ icon: 'wysiwyg-link', class: '', external: false, text: 'payouts_verify_modal_btn' }];

  public myTrafficSourcesSection: IEnlargeSectionData = {
    name: 'my_traffic_sources',
    icon: 'wysiwyg-link',
    section: 'trafficSources',
    negative: 'information'
  };

  public informationSection: IEnlargeSectionData = {
    name: 'informations',
    icon: 'news',
    section: 'information',
    negative: 'trafficSources'
  };

  public addTrafficSourceAlert: IAlert = {
    headerText: 'how_to_add_traffic_source',
    infoText: 'how_to_add_traffic_source_desc',
    buttonText: 'go_to_video',
    image: 'https://static2.mylead.global/img/publisher/video2.png',
    imageClass: 'video',
    link: 'https://www.youtube.com/watch?v=Dhmqo7cMZGE'
  };

  public comparisonEnginesAlert: IAlert = {
    headerText: 'no_traffic_source_question',
    infoText: 'no_traffic_source_question_desc',
    buttonText: 'go_to_program_comparison',
    image: 'https://static2.mylead.global/img/publisher/compare.png',
    imageClass: 'compare'
  };

  public addTrafficSourceTypesOfFields: { system_name: string; type: string }[] = [
    { system_name: 'url', type: 'textInput' },
    { system_name: 'name', type: 'textInput' },
    { system_name: 'views', type: 'numberInput' },
    { system_name: 'database_size', type: 'numberInput' },
    { system_name: 'download', type: 'numberInput' },
    { system_name: 'publishers', type: 'numberInput' },
    { system_name: 'followers', type: 'numberInput' },
    { system_name: 'subscriptions', type: 'numberInput' },
    { system_name: 'karma_number', type: 'numberInput' },
    { system_name: 'likes', type: 'numberInput' },
    { system_name: 'post_number', type: 'numberInput' },
    { system_name: 'relations_number', type: 'numberInput' },
    { system_name: 'group_channel_number', type: 'numberInput' },
    { system_name: 'channel_number', type: 'numberInput' },
    { system_name: 'members_biggest_channel', type: 'numberInput' },
    { system_name: 'friends', type: 'numberInput' },
    { system_name: 'people_connected', type: 'numberInput' },
    { system_name: 'group_number', type: 'numberInput' },
    { system_name: 'members_biggest_group', type: 'numberInput' },
    { system_name: 'members', type: 'numberInput' },
    { system_name: 'destination_group_age', type: 'select' },
    { system_name: 'destination_group_sex', type: 'select' },
    { system_name: 'destination_group_countries', type: 'checkboxList' },
    { system_name: 'destination_group_population', type: 'select' },
    { system_name: 'visits_month', type: 'select' },
    { system_name: 'ad_platform', type: 'select' },
    { system_name: 'ad_platform_other', type: 'textInput' },
    { system_name: 'profil_type', type: 'select' },
    { system_name: 'description', type: 'textarea' },
    { system_name: 'is_adult', type: 'checkbox' },
    { system_name: 'categories', type: 'checkboxTree' }
  ];

  private trafficSourcesListParamsSub$ = new BehaviorSubject<TrafficSourcesListParams>({ page: 1, per_page: 10 });
  private isTrafficSourceAdded = new BehaviorSubject(false);
  private httpOptions: object | undefined;
  public isTrafficSourceAdded$ = this.isTrafficSourceAdded.asObservable();

  constructor(
    private http: HttpClient,
    private translate: TranslateService
  ) {}

  public setTrafficSourcesListParams = (params: TrafficSourcesListParams) => {
    this.trafficSourcesListParamsSub$.next(params);
  };

  public watchTrafficSourcesListParams = (): Observable<TrafficSourcesListParams> => {
    return this.trafficSourcesListParamsSub$.asObservable();
  };

  public shareTrafficSourceAddStatus(status: boolean): void {
    this.isTrafficSourceAdded.next(status);
  }

  /** Retrieve user traffic source simple list */
  public getAllTrafficSources(only_accepted: boolean = false): Observable<BaseApiResponse<ISimpleTrafficSourcesResponse>> {
    this.httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      })
    };
    let params = new HttpParams();
    if (only_accepted) {
      params = params.set('only_accepted', only_accepted);
    }

    return this.http.get<BaseApiResponse<ISimpleTrafficSourcesResponse>>(environment.apiUrl + `/traffic-source/simple-list`, {
      ...this.httpOptions,
      params
    });
  }

  public getActiveCampaigns(
    search: string,
    sortValues: ISortMethodData,
    page?: number,
    perPage?: number
  ): Observable<PageableApiResponse<{ traffic_source_campaigns?: ITrafficSourceCampaignData[] }>> {
    this.httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      })
    };

    let params = new HttpParams();
    if (search) {
      params = params.set('search', search);
    }
    if (page) {
      params = params.set('page', page);
    }
    if (perPage) {
      params = params.set('perPage', perPage);
    }
    params = params.set('order_field', sortValues.order_field);
    params = params.set('order_type', sortValues.order_type);

    return this.http.get<PageableApiResponse<{ traffic_source_campaigns?: ITrafficSourceCampaignData[] }>>(environment.apiUrl + `/traffic-source/campaigns`, {
      params
    });
  }

  public getModalConfig(): Observable<BaseApiResponse<{ traffic_sources?: ICreateTrafficSourceData[] }>> {
    this.httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      })
    };

    return this.http.get<BaseApiResponse<{ traffic_sources?: ICreateTrafficSourceData[] }>>(environment.apiUrl + `/traffic-source/create`, this.httpOptions);
  }

  /** Retrieve user traffic source list */
  public getAllTrafficSourcesList(params: TrafficSourcesListParams): Observable<PageableApiResponse<{ traffic_sources?: ITrafficSource[] }>> {
    this.httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      }),
      params
    };

    return this.http.get<PageableApiResponse<{ traffic_sources?: ITrafficSource[] }>>(environment.apiUrl + `/traffic-source/list`, this.httpOptions);
  }

  /**
   * @description this method is not implemented yet
   * also the component which use it is probably not in use
   */
  public addTrafficSource(name: string, trackingDomain: string): Observable<any> {
    const trafficSource = {
      id: 1,
      name,
      trackingDomain
    };

    return this.http.put(environment.apiUrl + `/configurations/traffic-sources`, trafficSource);
  }

  public saveNewTrafficSource(fieldsInfo: object): Observable<BaseApiResponse<{ traffic_source?: { created_at?: string; updated_at?: string; id?: number } }>> {
    if (((fieldsInfo as any).ad_platform_other || (fieldsInfo as any).ad_platform_other === '') && (fieldsInfo as any).ad_platform != 2) {
      delete (fieldsInfo as any).ad_platform_other;
    }
    return this.http.post<BaseApiResponse<{ traffic_source?: { created_at?: string; updated_at?: string; id?: number } }>>(
      environment.apiUrl + `/traffic-source/store`,
      fieldsInfo
    );
  }

  public addTrafficSourceToCampaign(programId: number, trafficSourceId: number): Observable<BaseApiResponse<{ message?: string }>> {
    return this.http.post<BaseApiResponse<{ message?: string }>>(
      environment.apiUrl + `/campaigns/${programId}/traffic_source/store?traffic_source_id=${trafficSourceId}`,
      trafficSourceId
    );
  }

  public removeTrafficSourceFromCampaign(programId: number, trafficSourceId: number): Observable<BaseApiResponse<{ message?: string }>> {
    return this.http.delete<BaseApiResponse<{ message?: string }>>(environment.apiUrl + `/campaigns/${programId}/traffic_source/${trafficSourceId}/delete`);
  }

  public getTrafficSourcesAssignedToCampaign(campaignId: number): Observable<BaseApiResponse<ISimpleTrafficSourcesResponse>> {
    this.httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      })
    };

    return this.http.get<BaseApiResponse<ISimpleTrafficSourcesResponse>>(
      environment.apiUrl + `/campaigns/${campaignId}/traffic_source/added`,
      this.httpOptions
    );
  }

  public getTrafficSourceVerificationToken(
    trafficSourceId: number
  ): Observable<BaseApiResponse<{ verification?: { guid?: string; traffic_source_id?: number } }>> {
    let params = new HttpParams();
    params = params.set('traffic_source_id', trafficSourceId);
    return this.http.get<BaseApiResponse<{ verification?: { guid?: string; traffic_source_id?: number } }>>(
      environment.apiUrl + '/traffic-source/verification',
      { params }
    );
  }

  public checkTrafficSourceVerification(traffic_source_id: number, custom_url?: string): Observable<BaseApiResponse<{ status?: boolean }>> {
    let params = new HttpParams();
    params = params.set('traffic_source_id', traffic_source_id);
    if (custom_url) {
      params = params.set('custom_url', custom_url);
    }
    return this.http.post<BaseApiResponse<{ status?: boolean }>>(environment.apiUrl + '/traffic-source/verification/check', params);
  }

  public getDatOfTrafficSourceToEdit(trafficSourceId: number): Observable<BaseApiResponse<{ traffic_source?: IEditTrafficSourceData }>> {
    return this.http.get<BaseApiResponse<{ traffic_source?: IEditTrafficSourceData }>>(environment.apiUrl + `/traffic-source/${trafficSourceId}/edit`);
  }

  public updateTrafficSource(
    trafficSourceId: number,
    fieldsInfo: Record<string, any>
  ): Observable<BaseApiResponse<{ traffic_source?: { id?: number; updated_at?: string } }>> {
    if (((fieldsInfo as any).ad_platform_other || (fieldsInfo as any).ad_platform_other === '') && (fieldsInfo as any).ad_platform != 2) {
      delete (fieldsInfo as any).ad_platform_other;
    }
    return this.http.post<BaseApiResponse<{ traffic_source?: { id?: number; updated_at?: string } }>>(
      environment.apiUrl + `/traffic-source/${trafficSourceId}/update`,
      fieldsInfo
    );
  }

  public removeTrafficSource(trafficSourceId: number): Observable<BaseApiResponse<{ message?: string }>> {
    return this.http.delete<BaseApiResponse<{ message?: string }>>(environment.apiUrl + `/traffic-source/delete`, {
      params: { traffic_source_id: trafficSourceId }
    });
  }

  public prepareTrafficSourceInfo = (faqSectionData: Faq, currentLang: string) => {
    const currentLangFAQ = faqSectionData[currentLang][0].contents;
    const FAQdescription: string[] = [];
    currentLangFAQ.map(item => {
      FAQdescription.push(`<h6 class="font-weight-bold">${item.title}</h6>`);
      FAQdescription.push(`<div <p class="description info-content">${item.content}</div>`);
    });

    const trafficSourceInformation = [
      {
        informationTitle: 'what_is_traffic_source',
        informationIcon: 'list',
        buttonLink: '',
        buttonText: '',
        description: [
          `<h5 class="info-header">${this.translate.instant('view_translations.traffic_source_explenation_1')}</h5>`,
          `<p class="description info-content">${this.translate.instant('view_translations.traffic_source_explenation_2')}</p>`,
          `<p class="description info-content">${this.translate.instant('view_translations.traffic_source_explenation_3')}</p>`
        ],
        class: 'welcome',
        singleButton: false
      },
      {
        informationTitle: 'how_to_add_traffic_source',
        informationIcon: 'youtube',
        buttonLink: '',
        buttonText: '',
        description: [
          `<div class="yt-wrapper"><iframe class="yt" src="https://www.youtube.com/embed/uoRT4kdlzh8?si=ppHjiHFHhjMUtgxz"
          title="YouTube video player"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          referrerpolicy="strict-origin-when-cross-origin" allowfullscreen></iframe></div>`
        ],
        class: 'video',
        singleButton: false
      },
      {
        informationTitle: 'faq',
        informationIcon: 'faq',
        buttonLink: '',
        buttonText: '',
        description: [...FAQdescription],
        class: 'faq',
        singleButton: false
      }
    ];

    return trafficSourceInformation;
  };

  public getTrafficSourceDetails = (trafficSourceId: number): Observable<TrafficSourceDetailsResponse> => {
    return this.http.get<TrafficSourceDetailsResponse>(environment.apiUrl + `/traffic-source/${trafficSourceId}/details`);
  };
}
